/**
 * Precedence for generated vs admin-authored narrative fields.
 * chart_text_overrides: location_name + chart_title + section key.
 */

export type NarrativeFieldKey =
  | 'overview_title'
  | 'overview_description'
  | 'overview_insights'
  | 'relevance_title'
  | 'relevance_description'
  | 'equity_title'
  | 'equity_description'
  | 'methodology_title'
  | 'methodology_description'
  | 'methodology_sources'
  | 'methodology_steps';

export const OVERRIDE_VIEW_PREFIX = 'overview_insights::view=';
export const OVERRIDE_ALL_VIEWS_KEY = 'overview_insights::scope=all_views';
export const LEGACY_INSIGHTS_KEY = 'overview_insights';

export function scopedInsightsKey(viewId: string): string {
  return `${OVERRIDE_VIEW_PREFIX}${viewId}`;
}

export function resolveNarrativeText(
  fieldKey: NarrativeFieldKey,
  generatedDefault: string,
  overrides: Record<string, string>
): string {
  const custom = overrides[fieldKey]?.trim();
  if (custom) return custom;
  return generatedDefault;
}

/**
 * Key Insights precedence when chart has view-specific generated narrative:
 * 1) overview_insights::view={viewId}
 * 2) overview_insights::scope=all_views (explicit chart-wide)
 * 3) generated default
 *
 * Legacy bare `overview_insights` is NOT applied when viewId is set (avoids masking all tabs).
 * Legacy applies only when viewId is omitted (single-view charts).
 */
export function resolveKeyInsights(
  generated: string,
  overrides: Record<string, string>,
  viewId?: string | null
): string {
  if (viewId) {
    const viewScoped = overrides[scopedInsightsKey(viewId)]?.trim();
    if (viewScoped) return viewScoped;
    const allViews = overrides[OVERRIDE_ALL_VIEWS_KEY]?.trim();
    if (allViews) return allViews;
    return generated;
  }
  const legacy = overrides[LEGACY_INSIGHTS_KEY]?.trim();
  return legacy || generated;
}

export function insightsOverrideStorageKey(
  viewId: string | null | undefined,
  scope: 'view' | 'all_views'
): string {
  if (scope === 'all_views') return OVERRIDE_ALL_VIEWS_KEY;
  if (viewId) return scopedInsightsKey(viewId);
  return LEGACY_INSIGHTS_KEY;
}

export const OVERRIDE_SCOPE_LIMITATION =
  'Use overview_insights::view={id} for one tab, or overview_insights::scope=all_views for every tab. Legacy overview_insights applies only on charts without a narrative view id.';
